import { useEffect, type ReactNode } from 'react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}

export function Modal({ open, onClose, title, children, footer, width = 420 }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,30,61,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
        animation: 'fadeIn .12s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: width,
          background: '#fff',
          borderRadius: 14,
          boxShadow: '0 20px 60px rgba(15,30,61,.25)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 32px)',
        }}
      >
        {title && (
          <div style={{ padding: '18px 22px 12px', borderBottom: '1px solid rgba(15,30,61,.06)' }}>
            <h3
              id="modal-title"
              className="serif"
              style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}
            >
              {title}
            </h3>
          </div>
        )}
        <div style={{ padding: '16px 22px', overflow: 'auto', flex: 1, fontSize: 14, lineHeight: 1.5, color: '#1a2744' }}>
          {children}
        </div>
        {footer && (
          <div
            style={{
              padding: '12px 22px 16px',
              borderTop: '1px solid rgba(15,30,61,.06)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
